import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadOffIcon from "@mui/icons-material/FileDownloadOff";
import { Grid, Tooltip, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import React, { useState, useEffect, useMemo } from "react";
import Button from "../../components/Button/Button";
import Modal from "../../components/Modal/Modal.js";
import {
  ButtonGroup,
  StyledDialogContent,
} from "../../components/FormValidation/FormValidationStyles.js";
import CustomTable from "../../components/Table/Table.js";
import formFields from "../../config/formFields.json";
import { useAuth } from "../../contexts/AuthContext.js";
import {
  useUserReport,
  useExtractData,
  usePresignedURL,
} from "../../hooks/useExtractData.js";
import { renderFormField } from "../../utils/formFieldRenderer.js";
import { createFieldValidator } from "../../utils/validationHelper.js";
import { getEnumLabel, getEnumOptions } from "../../utils/enumUtils.js";
import moment from "moment-timezone";
import { useResourceApi } from "../../hooks/useResourceApi.js";
import { useProjectApi } from "../../hooks/useProjectApi.js";
import { useProjectResorceMappingApi } from "../../hooks/useProjectResource.js";
import { useTimesheetApi } from "../../hooks/useTimesheetApi.js";
import { useRbac } from "../../hooks/RbacContext";
import { ENV } from "../../utils/configs.js";

export const ExtractReport = () => {
  const { user } = useAuth();
  const { checkAccess, permissions } = useRbac();
  const superAdminRoleIds = ENV.SUPER_ADMIN_ROLE_ID.split(",").map((id) => parseInt(id.trim()));
  const isSuperAdmin = superAdminRoleIds.includes(parseInt(permissions?.globalRole?.role_id));
  const {
    getDepartments,
    hasReportingHierarchy,
    getResourcesByReportingManagerId,
    cacheOperations: { readDepartments, writeDepartments },
  } = useResourceApi();

  const {
    getProjects,
    cacheOperations: { readProjects, writeProjects },
  } = useProjectApi();

  const { getResourcesIncludingPOAndPMByProjectId } = useProjectResorceMappingApi();
  const { getFridaysByYearAndMonth, getLatestAndOldestTimesheetLoggedDate } =
    useTimesheetApi();

  const [departmentOptions, setDepartmentOptions] = useState(() => {
    const cached = readDepartments();
    return cached?.length ? cached : [];
  });

  const [projectOptions, setProjectOptions] = useState(() => {
    const cached = readProjects(isSuperAdmin ? true : false);
    return cached?.length
      ? cached.map((project) => ({
          value: project.project_id.toString(),
          label: project.project_name,
        }))
      : [];
  });

  const [resourceOptions, setResourceOptions] = useState([]);
  const [projectManagerOptions, setProjectManagerOptions] = useState([]);
  const [weekOptions, setWeekOptions] = useState([]);
  const [isLoadingProjectResources, setIsLoadingProjectResources] = useState(false);

  const [timesheetDateRange, setTimesheetDateRange] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);
  const [monthOptions, setMonthOptions] = useState([]);

  const resourceId = user?.userDetails?.resource_id;
  const [showTeamUtilizationReport, setShowTeamUtilizationReport] =
    useState(false);
  const [noReporteesDialogOpen, setNoReporteesDialogOpen] = useState(false);

  const { reports, refetch } = useUserReport(resourceId);

  useEffect(() => {
    if (resourceId) {
      refetch();
    }
  }, [resourceId]);

  useEffect(() => {
    const refreshDepartments = async () => {
      const result = await getDepartments();
      if (result.success && Array.isArray(result.data)) {
        const options = [
          // { label: "All", value: "ALL" },
          ...result.data.map((dept) => ({
            label: dept,
            value: dept,
          })),
        ];
        setDepartmentOptions(options);
        writeDepartments(options);
      }
    };

    refreshDepartments(); // always refresh
  }, []);

  useEffect(() => {
    const refreshProjects = async () => {
      const result = await getProjects(isSuperAdmin ? true : false);
      if (result.success && Array.isArray(result.data)) {
        const options = result.data.map((project) => ({
          value: project.project_id.toString(),
          label: project.project_name,
        }));
        setProjectOptions(options);
        writeProjects(result.data, true);
      }
    };

    refreshProjects();
  }, []);

  useEffect(() => {
    const fetchDateRange = async () => {
      const result = await getLatestAndOldestTimesheetLoggedDate();
      if (result.success && result.data) {
        const { oldest_logged_date, latest_logged_date } = result.data;
        setTimesheetDateRange({ oldest_logged_date, latest_logged_date });

        const startYear = new Date(oldest_logged_date).getFullYear();
        const endYear = new Date(latest_logged_date).getFullYear();
        const years = [];
        for (let y = startYear; y <= endYear; y++) {
          years.push({ label: String(y), value: y });
        }
        setYearOptions(years);
      }
    };
    fetchDateRange();
  }, []);

  const computeMonthsForYear = (selectedYear) => {
    if (!timesheetDateRange) return [];
    const { oldest_logged_date, latest_logged_date } = timesheetDateRange;
    const oldest = new Date(oldest_logged_date);
    const latest = new Date(latest_logged_date);
    const year = Number(selectedYear);

    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];

    let startMonth = year === oldest.getFullYear() ? oldest.getMonth() + 1 : 1;
    let endMonth = year === latest.getFullYear() ? latest.getMonth() + 1 : 12;

    const months = [];
    for (let m = startMonth; m <= endMonth; m++) {
      const val = String(m).padStart(2, "0");
      months.push({ label: monthNames[m - 1], value: val });
    }
    return months;
  };

  const reportAccess = permissions?.globalRole?.permissions?.access.filter(
    (value) => value.module == "reports"
  );

  const reportModuleAccess = reportAccess.map((val) =>
    val?.children.filter((val) => val.value == true)
  );

  const allowedKeys = reportModuleAccess[0]
    .filter((p) => p.value === true)
    .map((p) => p.name);

  // Step 2: Filter options where the value matches allowed permission keys
  const matchedOptions = getEnumOptions("report_type_code").filter((option) =>
    allowedKeys.includes(option.label)
  );

  useEffect(() => {
    const checkHierarchy = async () => {
      if (!resourceId) {
        setShowTeamUtilizationReport(false);
        return;
      }
      const result = await hasReportingHierarchy(resourceId);
      setShowTeamUtilizationReport(Boolean(result?.success && result?.data));
    };
    void checkHierarchy();
  }, [resourceId]);

  const optionDropdown = useMemo(() => {
    const base = Array.isArray(matchedOptions) ? [...matchedOptions] : [];
    // Add "Team Utilization Report" only when RBAC permission exists
    const value = "TEAM_UTILIZATION_REPORT";
    const label = "Team Utilization Report";

    // RBAC: only show if permission exists for this report
    if (!allowedKeys?.includes(label)) return base;

    if (base.some((o) => o?.value === value)) return base;
    base.push({ label, value });
    return base;
  }, [matchedOptions, showTeamUtilizationReport, allowedKeys]);

  const extractReportFields = formFields.extract_report?.basic.fields;

  const initialData = useMemo(
    () =>
      Object.entries(extractReportFields).reduce((acc, [key, field]) => {
        acc[key] = field.multiple ? [] : "";
        return acc;
      }, {}),
    [extractReportFields]
  );
  const validationSchema = useMemo(
    () =>
      Yup.object().shape(
        Object.entries(extractReportFields).reduce((acc, [key, field]) => {
          acc[key] = createFieldValidator(field, extractReportFields);
          return acc;
        }, {})
      ),
    [extractReportFields]
  );

  const getOptionsForField = (fieldName) => {
    switch (fieldName) {
      case "reports":
        return optionDropdown;
      case "departments":
        return departmentOptions;
      case "projects":
        return projectOptions;
      case "resources":
        return [...resourceOptions].sort((a, b) =>
          a.label.localeCompare(b.label)
        );
      case "project_managers":
        return [...projectManagerOptions].sort((a, b) =>
          a.label.localeCompare(b.label)
        );
      case "weeks":
        return weekOptions;
      case "available_years":
        return yearOptions;
      case "available_months":
        return monthOptions;
      default:
        return [];
    }
  };

  const normalizeDepartmentsForApi = (dept) => {
    if (Array.isArray(dept)) {
      return dept.map((x) => String(x).trim()).filter((s) => s && s !== "ALL");
    }
    if (dept === "" || dept == null) return [];
    const s = String(dept).trim();
    return s && s !== "ALL" ? [s] : [];
  };

  const normalizeProjectIds = (v) => {
    if (Array.isArray(v)) {
      return v.filter((id) => id !== "" && id != null).map(String);
    }
    if (v !== "" && v != null) return [String(v)];
    return [];
  };

  const fetchResourcesForSelectedProjects = async (
    projectIdsValue,
    departmentSelection
  ) => {
    const ids = normalizeProjectIds(projectIdsValue);
    const departments = normalizeDepartmentsForApi(departmentSelection);
    if (ids.length === 0) {
      setResourceOptions([]);
      setProjectManagerOptions([]);
      return;
    }
    setIsLoadingProjectResources(true);
    try {
      const [resourceResults, pmResults] = await Promise.all([
        Promise.all(
          ids.map((id) =>
            getResourcesIncludingPOAndPMByProjectId(
              parseInt(id, 10),
              false,
              departments
            )
          )
        ),
        Promise.all(
          ids.map((id) =>
            getResourcesIncludingPOAndPMByProjectId(parseInt(id, 10), true)
          )
        ),
      ]);

      const mergeResourceMaps = (results) => {
        const merged = new Map();
        results.forEach((r) => {
          if (r.success && Array.isArray(r.data)) {
            r.data.forEach((res) => {
              merged.set(String(res.resource_id), res);
            });
          }
        });
        return [...merged.values()].map((resource) => ({
          value: resource.resource_id.toString(),
          label:
            `${resource.first_name ?? ""} ${resource.last_name ?? ""}`.trim() ||
            resource.username,
        }));
      };

      setResourceOptions(mergeResourceMaps(resourceResults));
      setProjectManagerOptions(mergeResourceMaps(pmResults));
    } finally {
      setIsLoadingProjectResources(false);
    }
  };

  const handleCustomChange = (
    fieldName,
    value,
    formData,
    setFieldValue,
    setFieldTouched
  ) => {
    setFieldValue(fieldName, value, false);
    setFieldTouched(fieldName, true, false);

    if (fieldName === "kpi") {
      setResourceOptions([]);
      setProjectManagerOptions([]);
      setWeekOptions([]);
      setMonthOptions([]);

      // Team Utilization: populate Resource dropdown with reporting hierarchy
      if (value === "TEAM_UTILIZATION_REPORT" && resourceId) {
        void (async () => {
          // hierarchy gate: show message if no reportees
          const hierarchyResult = await hasReportingHierarchy(resourceId);
          const hasHierarchy = Boolean(
            hierarchyResult?.success && hierarchyResult?.data
          );

          if (!hasHierarchy) {
            setNoReporteesDialogOpen(true);
            setFieldValue("kpi", "", false);
            setFieldTouched("kpi", false, false);
            return;
          }

          const res = await getResourcesByReportingManagerId(resourceId);
          if (res?.success && Array.isArray(res.data)) {
            const options = res.data
              .map((r) => ({
                value: r.resource_id?.toString(),
                label:
                  `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() ||
                  r.username,
              }))
              .filter((o) => o.value);
            setResourceOptions(
              options.sort((a, b) => a.label.localeCompare(b.label))
            );
          } else {
            setResourceOptions([]);
          }
        })();
      } else {
        // Non-team reports: clear team resource list
        setResourceOptions([]);
      }
    }

    if (fieldName === "year") {
      setMonthOptions(
        value !== "" && value != null ? computeMonthsForYear(value) : []
      );
      setWeekOptions([]);
    }

    const fieldConfig = formFields?.extract_report?.basic.fields[fieldName];
    if (fieldConfig) {
      Object.entries(formFields.extract_report?.basic.fields).forEach(
        ([field, config]) => {
          if (config.dependsOn?.field === fieldName) {
            if (config.dependsOn.clearOnChange) {
              setFieldValue(field, config.multiple ? [] : "", false);
              setFieldTouched(field, false, false);
            }
          }
        }
      );

      if (fieldConfig.affects) {
        fieldConfig.affects.forEach((affectedField) => {
          const affectedConfig =
            formFields.extract_report?.basic.fields[affectedField];
          setFieldValue(
            affectedField,
            affectedConfig?.multiple ? [] : ""
          );
          setFieldTouched(affectedField, false);
        });
      }
    }

    if (fieldName === "project_id") {
      void fetchResourcesForSelectedProjects(value, formData?.department);
    }

    if (fieldName === "department") {
      void fetchResourcesForSelectedProjects(formData?.project_id, value);
    }

    if (fieldName === "month") {
      void (async () => {
        const yearValue = formData?.year;
        const year =
          yearValue === "" || yearValue == null ? "" : String(yearValue).trim();

        const months = Array.isArray(value)
          ? value.filter((m) => m !== "" && m != null).map(String)
          : value === "" || value == null
          ? []
          : [String(value)];

        if (!year || months.length === 0) {
          setWeekOptions([]);
          return;
        }

        const results = await Promise.all(
          months.map((m) => getFridaysByYearAndMonth(`${year}_${m}`))
        );

        const merged = new Map();
        results.forEach((r) => {
          if (r?.success && Array.isArray(r.data)) {
            r.data.forEach((w) => {
              if (w?.value != null) merged.set(String(w.value), w);
            });
          }
        });

        const nextFridayInclusive = (dateStr) => {
          const d = new Date(dateStr);
          // JS: 0=Sun ... 5=Fri
          const day = d.getDay();
          const daysUntilFriday = (5 - day + 7) % 7; // 0 if already Friday
          const f = new Date(d);
          f.setDate(d.getDate() + daysUntilFriday);
          f.setHours(0, 0, 0, 0);
          return f;
        };

        const withinLoggedRange = (isoDate) => {
          if (
            !timesheetDateRange?.oldest_logged_date ||
            !timesheetDateRange?.latest_logged_date
          ) {
            return true;
          }
          const d = new Date(isoDate);
          d.setHours(0, 0, 0, 0);

          // Weeks are Friday week-ending dates, so clamp the range
          // to the Friday of the week containing oldest/latest logged dates.
          const startFriday = nextFridayInclusive(
            timesheetDateRange.oldest_logged_date
          );
          const endFriday = nextFridayInclusive(
            timesheetDateRange.latest_logged_date
          );

          return d >= startFriday && d <= endFriday;
        };

        const weeks = [...merged.values()]
          .filter((w) => w?.value && withinLoggedRange(w.value))
          .map((w) => ({
            label: w.label,
            value: w.value,
          }));

        setWeekOptions(weeks);
      })();
    }
  };

  const [selectedValue, setSelectedValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { generateFile } = useExtractData();
  const { generatePresignedURL } = usePresignedURL();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDropdown = (value) => {
    setSelectedValue(value);
  };

  const handleSubmit = async (event, { setSubmitting }) => {
    const toPositiveIntArray = (v) => {
      if (Array.isArray(v)) {
        return [
          ...new Set(
            v
              .map((x) => parseInt(x, 10))
              .filter((n) => Number.isFinite(n) && n > 0)
          ),
        ];
      }
      if (v === "" || v == null) return undefined;
      const n = parseInt(v, 10);
      return Number.isFinite(n) && n > 0 ? [n] : undefined;
    };
    const toIsoDateStringArray = (v) => {
      if (Array.isArray(v)) {
        return v
          .map((x) => (x != null ? String(x).trim() : ""))
          .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
      }
      if (v === "" || v == null) return undefined;
      const s = String(v).trim();
      return /^\d{4}-\d{2}-\d{2}$/.test(s) ? [s] : undefined;
    };
    const toPositiveStringArray = (v) => {
      if (Array.isArray(v)) {
        return [...new Set(v.map((x) => String(x).trim()))];
      }
      if (v === "" || v == null) return undefined;
      const s = String(v).trim();
      return s ? [s] : undefined;
    };

    const filters = {
      startDate: event.start_date,
      endDate: event.end_date,
      departments: toPositiveStringArray(event.department),
      projectIds: toPositiveIntArray(event.project_id),
      resourceIds: toPositiveIntArray(event.resource_id),
      projectManagerIds: toPositiveIntArray(event.project_manager),
      weekDates: toIsoDateStringArray(event.week),
      yearAndQuarter: event.year_and_quarter || undefined,
    };
    try {
      setSubmitting(true);
      const result = await generateFile(event.kpi, filters);
      if (result?.GetExportData?.success) {
        await refetch();
      } else {
        await refetch();
      }
    } catch (error) {
      await refetch();
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      id: "report_name",
      label: "REPORT NAME",
    },
    {
      id: "report_status",
      label: "FILE STATUS",
    },
    {
      id: "created_on",
      label: "DATE OF REQUEST",
    },
    // {
    //   id: "updated_on",
    //   label: "GENERATED ON",
    // },
    {
      id: "file_Path",
      label: "Download",
      render: (value, row) => {
        const isDownload = value !== "";
        return (
          <Tooltip title={isDownload ? "Download" : "Failed"} arrow>
            {isDownload ? (
              <DownloadIcon
                onClick={() => handleDownload(row)}
                sx={{ color: "green", cursor: "pointer" }}
              />
            ) : (
              <FileDownloadOffIcon />
            )}
          </Tooltip>
        );
      },
      align: "center",
    },
  ];

  const onClose = () => {
    setIsOpen(false);
    setAnchorEl(null);
  };

  const cleanFileName = (fileName) => {
    return fileName.replace(/^reports\//, "").replace(/_uuid_[\w-]+/, "");
  };

  const handleDownload = async (row) => {
    try {
      const { GetPresignedURL } = await generatePresignedURL(row?.file_path);
      const fullURL = `${
        "https://" +
        process.env.REACT_APP_MINIO_OBJECT_END_POINT +
        "/" +
        window.location.hostname.replace(/\./g, "-") +
        "/" +
        GetPresignedURL
      }`;
      const link = document.createElement("a");
      link.href = fullURL;
      link.download = row?.report_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <>
      <Typography>Reports</Typography>
      {noReporteesDialogOpen ? (
        <Modal
          type="info"
          title="Access Required"
          message={`You don't have any reportees assigned in the company. This report is only available for users with assigned reportees.\n\nIf you believe this is incorrect, please contact your administrator, Resource_Management@solutionec.com`}
          onClose={() => setNoReporteesDialogOpen(false)}
        />
      ) : null}
      <Formik
        initialValues={initialData || {}}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnMount={false}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({
          values,
          errors,
          touched,
          handleChange: formikHandleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          resetForm,
          isSubmitting,
        }) => (
          <Form noValidate>
            <div style={{ display: "none" }}>
              <input type="text" name="fake_user" autoComplete="username" />
              <input
                type="password"
                name="fake_pass"
                autoComplete="current-password"
              />
            </div>
            <StyledDialogContent>
              <Grid container spacing={1.5} sx={{ width: "100%", m: 0 }}>
                {Object.entries(formFields.extract_report?.basic.fields).map(
                  ([fieldName, fieldConfig]) => {
                    let fieldOptions = [];
                    const isTeamUtilization =
                      values?.kpi === "TEAM_UTILIZATION_REPORT";

                    // Hide these fields completely for Team Utilization
                    if (
                      isTeamUtilization &&
                      (fieldName === "department" ||
                        fieldName === "project_id" ||
                        fieldName === "project_manager")
                    ) {
                      return null;
                    }

                    // For Team Utilization, Resource should be shown without project dependency.
                    // (formFields.json uses dependsOn: project_id, which doesn't apply here.)
                    const overrideDependsOnForTeam =
                      isTeamUtilization &&
                      (fieldName === "resource_id" ||
                        fieldName === "project_id" ||
                        fieldName === "project_manager");

                    const baseConfig = overrideDependsOnForTeam
                      ? { ...fieldConfig, dependsOn: null }
                      : fieldConfig;

                    const effectiveFieldConfig =
                      (fieldName === "project_manager" || fieldName === "resource_id") &&
                      isLoadingProjectResources
                        ? {
                            ...baseConfig,
                            disabled: true,
                            placeholder: "Loading...",
                          }
                        : baseConfig;

                    if (effectiveFieldConfig.source) {
                      fieldOptions = getOptionsForField(effectiveFieldConfig.source);
                    } else if (effectiveFieldConfig.fields) {
                      fieldOptions = Object.entries(effectiveFieldConfig.fields).reduce(
                        (acc, [subFieldName, subFieldConfig]) => {
                          if (subFieldConfig.source) {
                            acc[subFieldConfig.source] = getOptionsForField(
                              subFieldConfig.source
                            );
                          }
                          return acc;
                        },
                        {}
                      );
                    }

                    return renderFormField({
                      fieldName,
                      fieldConfig: effectiveFieldConfig,
                      formData: values,
                      errors,
                      touched,
                      handleChange: (fieldName, value) => {
                        handleCustomChange(
                          fieldName,
                          value,
                          values,
                          setFieldValue,
                          setFieldTouched
                        );
                      },
                      handleBlur,
                      setFieldValue,
                      setFieldTouched,
                      options: fieldOptions,
                      moduleName: "reports",
                    });
                  }
                )}
              </Grid>

              <ButtonGroup>
                <Button
                  variant="outlined"
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  disabled={isSubmitting}
                  size="small"
                  fullWidth
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={isSubmitting}
                  fullWidth
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={18} color="inherit" />
                      <span style={{ marginLeft: 8 }}>Requesting...</span>
                    </>
                  ) : (
                    "Request Report"
                  )}
                </Button>
              </ButtonGroup>
            </StyledDialogContent>
          </Form>
        )}
      </Formik>
      {reports?.length ? (
        <CustomTable
          columns={columns}
          data={reports.map((report) => ({
            id: report.report_id,
            report_name: cleanFileName(report.report_name),
            report_status: getEnumLabel(
              "report_status_code",
              report.report_status
            ),
            created_on: moment(report.created_on).format("MM/DD/YYYY hh:mm A"),
            // updated_on: moment(report.updated_on).format("MM/DD/YYYY hh:mm A"),
            file_path: report.file_path,
          }))}
          showActions={false}
          showAddButton={false}
          addButtonDisable={true}
          pageSize={10}
          stickyHeader={true}
          title="Download Reports"
          subtitle=""
          alignment="left"
          fontSize="12px"
          showFilters={false}
        />
      ) : null}
    </>
  );
};
